import type {
    AbsenceAllowanceSchema,
    CreateAbsenceAllowanceDtoSchema,
    CreateFtePeriodDtoSchema,
    CreateIndividualDtoSchema,
    FtePeriodSchema,
    IndividualSchema,
    UpdateAbsenceAllowanceDtoSchema,
    UpdateFtePeriodDtoSchema,
    UpdateIndividualDtoSchema,
} from "@nexttrack/api-schema";

import { rootApi } from "@/store/services/root-api";

export const individualsApi = rootApi.injectEndpoints({
    endpoints: (builder) => ({
        getIndividuals: builder.query<IndividualSchema[], { teamId?: string; departmentId?: string } | void>({
            query: (params) => ({ url: "individuals", params: params ?? {} }),
            providesTags: ["Individual"],
        }),
        createIndividual: builder.mutation<IndividualSchema, CreateIndividualDtoSchema>({
            query: (body) => ({ url: "individuals", method: "POST", body }),
            invalidatesTags: ["Individual"],
        }),
        updateIndividual: builder.mutation<IndividualSchema, { id: string; data: UpdateIndividualDtoSchema }>({
            query: ({ id, data }) => ({ url: `individuals/${id}`, method: "PATCH", body: data }),
            invalidatesTags: ["Individual", "Timeline"],
        }),
        archiveIndividual: builder.mutation<void, { id: string; archiveDate: string }>({
            query: ({ id, archiveDate }) => ({ url: `individuals/${id}`, method: "DELETE", body: { archiveDate } }),
            invalidatesTags: ["Individual", "Timeline"],
        }),
        addFtePeriod: builder.mutation<FtePeriodSchema, { individualId: string; data: CreateFtePeriodDtoSchema }>({
            query: ({ individualId, data }) => ({
                url: `individuals/${individualId}/fte-periods`,
                method: "POST",
                body: data,
            }),
            invalidatesTags: ["Individual", "Timeline"],
        }),
        updateFtePeriod: builder.mutation<
            FtePeriodSchema,
            { individualId: string; ftePeriodId: string; data: UpdateFtePeriodDtoSchema }
        >({
            query: ({ individualId, ftePeriodId, data }) => ({
                url: `individuals/${individualId}/fte-periods/${ftePeriodId}`,
                method: "PATCH",
                body: data,
            }),
            invalidatesTags: ["Individual", "Timeline"],
        }),
        deleteFtePeriod: builder.mutation<void, { individualId: string; ftePeriodId: string }>({
            query: ({ individualId, ftePeriodId }) => ({
                url: `individuals/${individualId}/fte-periods/${ftePeriodId}`,
                method: "DELETE",
            }),
            invalidatesTags: ["Individual", "Timeline"],
        }),
        addAbsenceAllowance: builder.mutation<
            AbsenceAllowanceSchema,
            { individualId: string; data: CreateAbsenceAllowanceDtoSchema }
        >({
            query: ({ individualId, data }) => ({
                url: `individuals/${individualId}/absence-allowances`,
                method: "POST",
                body: data,
            }),
            invalidatesTags: ["Individual", "Timeline"],
        }),
        updateAbsenceAllowance: builder.mutation<
            AbsenceAllowanceSchema,
            { individualId: string; allowanceId: string; data: UpdateAbsenceAllowanceDtoSchema }
        >({
            query: ({ individualId, allowanceId, data }) => ({
                url: `individuals/${individualId}/absence-allowances/${allowanceId}`,
                method: "PATCH",
                body: data,
            }),
            invalidatesTags: ["Individual", "Timeline"],
        }),
        deleteAbsenceAllowance: builder.mutation<void, { individualId: string; allowanceId: string }>({
            query: ({ individualId, allowanceId }) => ({
                url: `individuals/${individualId}/absence-allowances/${allowanceId}`,
                method: "DELETE",
            }),
            invalidatesTags: ["Individual", "Timeline"],
        }),
    }),
});

export const {
    useGetIndividualsQuery,
    useCreateIndividualMutation,
    useUpdateIndividualMutation,
    useArchiveIndividualMutation,
    useAddFtePeriodMutation,
    useUpdateFtePeriodMutation,
    useDeleteFtePeriodMutation,
    useAddAbsenceAllowanceMutation,
    useUpdateAbsenceAllowanceMutation,
    useDeleteAbsenceAllowanceMutation,
} = individualsApi;
